import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { AppEntry, Locale } from "../types";

interface Props {
  apps: AppEntry[];
  query: string;
  onQueryChange: (next: string) => void;
  category: string;
  onCategoryChange: (next: string) => void;
}

export function Toolbar({ apps, query, onQueryChange, category, onCategoryChange }: Props) {
  const { t, i18n } = useTranslation("launcher");
  const lng: Locale = i18n.resolvedLanguage === "en" ? "en" : "es";

  const { categoryIds, categoryLabels, counts } = useMemo(() => {
    const ids: string[] = ["all"];
    const labels = new Map<string, string>();
    const c: Record<string, number> = { all: apps.length };
    apps.forEach(a => {
      const id = a.category.en || a.category.es;
      if (!ids.includes(id)) ids.push(id);
      labels.set(id, a.category[lng] || a.category.es);
      c[id] = (c[id] ?? 0) + 1;
    });
    return { categoryIds: ids, categoryLabels: labels, counts: c };
  }, [apps, lng]);

  return (
    <div className="flex flex-wrap gap-2 items-center mb-4" role="region" aria-label={t("filter.ariaLabel")}>
      <label className="flex-1 min-w-[200px] basis-60 h-control flex items-center gap-1 px-2 bg-surface-subtle/40 border border-border-subtle rounded-sm transition-colors duration-150 focus-within:bg-surface-subtle/60 focus-within:border-brand-primary/60 focus-within:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)]">
        <span className="material-symbols-outlined text-[14px] text-text-muted/70" aria-hidden="true">
          search
        </span>
        <input
          type="search"
          value={query}
          onChange={e => onQueryChange(e.target.value)}
          placeholder={t("search.placeholder")}
          aria-label={t("search.ariaLabel")}
          autoComplete="off"
          className="flex-1 h-full bg-transparent border-0 outline-none text-text-main font-mono text-12 placeholder:text-text-muted/70"
          onKeyDown={e => {
            if (e.key === "Escape" && query) {
              e.preventDefault();
              onQueryChange("");
            }
          }}
        />
      </label>

      <div role="group" aria-label={t("filter.ariaLabel")} className="flex flex-wrap gap-1">
        {categoryIds.map(id => {
          const active = category === id;
          const label = id === "all" ? t("filter.all") : categoryLabels.get(id) ?? id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onCategoryChange(id)}
              aria-pressed={active}
              className={[
                "h-control px-2 inline-flex items-center gap-1 rounded-sm border text-12 font-bold uppercase tracking-widerx transition-colors duration-150",
                "focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)]",
                active
                  ? "text-brand-primary bg-brand-primary/15 border-brand-primary/30"
                  : "text-text-muted bg-surface-subtle/50 border-border-subtle hover:text-brand-primary hover:border-brand-primary/60",
              ].join(" ")}
            >
              <span>{label}</span>
              <span
                className={`font-mono text-10 font-medium tracking-normal ${active ? "text-brand-primary" : "text-text-muted/70"}`}
              >
                {counts[id] ?? 0}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
